import { z } from 'zod';
import { parse, format, addHours, isAfter, isBefore, setHours, setMinutes, startOfDay, addMonths } from 'date-fns';
import { RepeatType } from '../../types/form-data.types';
import type { VisitStepFieldsConfig } from '../../hooks/use-ui-metadata-field-config.hook';
import { MAX_MULTI_DAY_VISITS } from 'shared/consts';

/**
 * Parse time string in HH:mm format to a Date object (using today as base date)
 * @param timeString - Time string in HH:mm format (24-hour)
 * @returns Date object with the time set, or null if invalid
 */
const parseTime = (timeString: string): Date | null => {
  try {
    // Use a reference date (today) and parse the time string
    const referenceDate = startOfDay(new Date());
    const parsed = parse(timeString, 'HH:mm', referenceDate);

    // Check if parsing was successful (not Invalid Date)
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

/**
 * Check if a time string is valid and within 00:00-23:59 range
 * @param timeString - Time string in HH:mm format
 * @returns true if valid, false otherwise
 */
const isValidTime = (timeString: string): boolean => {
  const parsed = parseTime(timeString);
  if (!parsed) {
    return false;
  }

  // Check if the time is within valid range (00:00 to 23:59)
  const maxTime = setHours(setMinutes(startOfDay(new Date()), 59), 23);
  return !isAfter(parsed, maxTime);
};

/**
 * Compare two time strings
 * @param time1 - First time string in HH:mm format
 * @param time2 - Second time string in HH:mm format
 * @returns -1 if time1 < time2, 0 if equal, 1 if time1 > time2, or null if invalid
 */
const compareTimes = (time1: string, time2: string): number | null => {
  const parsed1 = parseTime(time1);
  const parsed2 = parseTime(time2);

  if (!parsed1 || !parsed2) {
    return null;
  }

  if (isBefore(parsed1, parsed2)) {
    return -1;
  }
  if (isAfter(parsed1, parsed2)) {
    return 1;
  }
  return 0;
};

/**
 * Calculate end time from start time and duration
 * @param startTime - Start time in HH:mm format
 * @param durationHours - Duration in hours
 * @returns End time in HH:mm format or null if exceeds 23:59
 */
const calculateEndTime = (startTime: string, durationHours: number): string | null => {
  const startDate = parseTime(startTime);
  if (!startDate) {
    return null;
  }

  const endDate = addHours(startDate, durationHours);
  const maxTime = setHours(setMinutes(startOfDay(new Date()), 59), 23);

  // Check if end time exceeds 23:59
  if (isAfter(endDate, maxTime)) {
    return null;
  }

  return format(endDate, 'HH:mm');
};

/**
 * Schema for when step validation
 * @param t - Translation function for error messages
 * @returns Zod schema object
 */
type WhenStepData = {
  visitStartDate: Date;
  visitEndDate?: Date | null;
  startTime: string;
  endTime?: string | null;
  duration?: number;
  useDuration?: boolean;
  repeatType: RepeatType;
  repeatEndDate?: Date | null;
  recurrenceDays?: {
    monday?: boolean;
    tuesday?: boolean;
    wednesday?: boolean;
    thursday?: boolean;
    friday?: boolean;
    saturday?: boolean;
    sunday?: boolean;
  };
  isMultiDay?: boolean;
  visitDates?: Date[];
};

export const createWhenStepSchema = (config: VisitStepFieldsConfig) =>
  z
    .object({
      visitStartDate: z.date(),
      visitEndDate: z.date().nullable().optional(),
      startTime: z
        .string()
        .min(1, 'errors.startTimeIsRequired')
        .refine(
          (val) => {
            if (!val) {
              return false;
            }
            return isValidTime(val);
          },
          {
            message: 'errors.startTimeIsRequired',
          },
        ),
      endTime: z.string().nullable().optional(),
      duration: z.number().positive().optional(),
      useDuration: z.boolean().optional(),
      repeatType: z.enum(RepeatType),
      repeatEndDate: z.date().nullable().optional(),
      recurrenceDays: z
        .object({
          monday: z.boolean().optional(),
          tuesday: z.boolean().optional(),
          wednesday: z.boolean().optional(),
          thursday: z.boolean().optional(),
          friday: z.boolean().optional(),
          saturday: z.boolean().optional(),
          sunday: z.boolean().optional(),
        })
        .optional(),
      isMultiDay: z.boolean().optional(),
      visitDates: z.array(z.date()).optional(),
    })
    .refine((data) => data.visitStartDate instanceof Date, {
      message: 'errors.visitStartDateIsRequired',
      path: ['visitStartDate'],
    })
    .refine(
      (data: WhenStepData) => {
        // If in duration mode, duration must be provided
        if (!config.visitDuration.hidden && data.useDuration) {
          return data.duration !== undefined && data.duration !== null && data.duration > 0;
        }
        // If in endTime mode, endTime must be provided
        return !!(data.endTime && data.endTime.trim().length > 0);
      },
      {
        message: config.visitDuration.hidden ? 'errors.endTimeIsRequired' : 'errors.endTimeOrDurationRequired',
        path: ['endTime'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // If endTime is provided, validate it
        if (data.endTime) {
          return isValidTime(data.endTime);
        }
        return true;
      },
      {
        message: 'errors.endTimeExceedsMax',
        path: ['endTime'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // If endTime is provided and startTime exists, check endTime > startTime
        if (data.endTime && data.startTime) {
          const comparison = compareTimes(data.startTime, data.endTime);
          if (comparison === null) {
            return false;
          }
          // endTime should be after startTime
          return comparison < 0;
        }
        return true;
      },
      {
        message: 'errors.endTimeShouldBeAfterStart',
        path: ['endTime'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // If duration is provided, validate it
        if (data.duration !== undefined && data.duration !== null) {
          // Duration must be positive
          if (data.duration <= 0) {
            return false;
          }

          // Calculate end time and check it doesn't exceed 23:59
          if (data.startTime) {
            const calculatedEndTime = calculateEndTime(data.startTime, data.duration);
            if (calculatedEndTime === null) {
              return false;
            }
          }
        }
        return true;
      },
      {
        message: 'errors.durationExceedsMax',
        path: ['duration'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // If duration is provided, check it's positive
        if (data.duration !== undefined && data.duration !== null && data.duration <= 0) {
          return false;
        }
        return true;
      },
      {
        message: 'errors.durationInvalid',
        path: ['duration'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // If repeatType is not NONE, repeatEndDate is required
        if (data.repeatType !== RepeatType.NONE && !data.repeatEndDate) {
          return false;
        }
        return true;
      },
      {
        message: 'errors.repeatEndDateIsRequired',
        path: ['repeatEndDate'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // repeatEndDate must be after visitStartDate (skip if not repeating)
        if (data.repeatType !== RepeatType.NONE && data.repeatEndDate && data.visitStartDate) {
          const endDateStart = startOfDay(data.repeatEndDate);
          const visitDateStart = startOfDay(data.visitStartDate);
          return isAfter(endDateStart, visitDateStart);
        }
        return true;
      },
      {
        message: 'errors.repeatEndDateShouldBeAfterVisitDate',
        path: ['repeatEndDate'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // repeatEndDate must not be more than 6 months from visitStartDate (skip if not repeating)
        if (data.repeatType !== RepeatType.NONE && data.repeatEndDate && data.visitStartDate) {
          const maxEndDate = addMonths(startOfDay(data.visitStartDate), 6);
          const endDateStart = startOfDay(data.repeatEndDate);
          return !isAfter(endDateStart, maxEndDate);
        }
        return true;
      },
      {
        message: 'errors.repeatEndDateExceedsMax',
        path: ['repeatEndDate'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        // For custom repeatType, at least one day must be selected
        if (data.repeatType === RepeatType.CUSTOM) {
          if (!data.recurrenceDays) {
            return false;
          }
          return Object.values(data.recurrenceDays).some((day) => day === true);
        }
        return true;
      },
      {
        message: 'errors.customRepeatDaysRequired',
        path: ['recurrenceDays'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        if (!data.isMultiDay) {
          return true;
        }
        return Array.isArray(data.visitDates) && data.visitDates.length > 0;
      },
      {
        message: 'errors.visitDatesRequired',
        path: ['visitDates'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        if (!data.isMultiDay || !data.visitDates) {
          return true;
        }
        return data.visitDates.length <= MAX_MULTI_DAY_VISITS;
      },
      {
        message: 'errors.visitDatesMax',
        path: ['visitDates'],
      },
    )
    .refine(
      (data: WhenStepData) => {
        if (!data.isMultiDay) {
          return true;
        }
        return data.repeatType === RepeatType.NONE;
      },
      {
        message: 'errors.visitDatesExclusiveWithRecurrence',
        path: ['repeatType'],
      },
    );
